import { Box, Grid } from "@mui/material"
import {
  FocusHandler,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, { memo, useCallback } from "react"

import useTranslate, { Namespaces } from "../../../hooks/use-translate"
import { s } from "../../../utilities/common"
import { theme, TI } from "../../../utilities/theme"
import { DGText } from "../../common/DGText"
import { Props, TTeaser } from "../Slider"

export const Collection = memo(
  ({ data, onFocus, onPressTitle, path, prefix }: Props & { path: string }) => {
    const me = path + prefix + data.id + data.title
    const t = useTranslate(Namespaces.Common)
    return (
      <>
        <DGText
          tx={TI.H07tv}
          sx={{
            color: theme.palette.white.main,
            pl: s("96px"),
            mb: s("45px"),
          }}
        >
          {t("page__artists_detail_selected_works_title")}
        </DGText>
        <Grid
          sx={{ pl: s("60px"), pr: s("60px"), mb: s("96px") }}
          key={"works-" + data.id}
          container
        >
          {data.teasers.map((teaser, index) => (
            <Item
              key={data.id + data.title + teaser.uniqueId + index}
              me={me}
              onPress={onPressTitle}
              onFocus={onFocus}
              teaser={teaser}
              index={index}
            />
          ))}
        </Grid>
      </>
    )
  },
  (prevProps, nextProps) => _.isEqual(prevProps.data, nextProps.data),
)

const Item = ({
  teaser,
  onFocus,
  onPress,
  me,
  index,
}: {
  teaser: TTeaser
  onFocus?: FocusHandler
  onPress: (data?: TTeaser, index?: number) => void
  me: string
  index: number
}) => {
  const onPressAction = useCallback(() => {
    onPress(teaser, index)
  }, [teaser, onPress])
  const { focused, ref, focusSelf } = useFocusable({
    focusKey: me + teaser.uniqueId,
    onFocus,
    onEnterPress: onPressAction,
  })
  return (
    <Box
      key={"workitem-" + teaser.id}
      sx={{ zIndex: focused ? 2 : 1, position: "relative" }}
    >
      <Box
        sx={{
          height: s("30px"),
          left: 0,
          top: s("-10px"),
          position: "absolute",
          width: s("600px"),
          borderTopLeftRadius: s("24px"),
          borderTopRightRadius: s("24px"),
          backgroundColor: focused
            ? theme.palette.secondary.variant
            : "transparent",
        }}
      ></Box>
      <Box
        onMouseEnter={() => focusSelf({ noScroll: true })}
        onMouseDown={onPressAction}
        aria-label={teaser.subtitle + ". " + teaser.title}
        tabIndex={0}
        ref={ref}
        sx={{
          pl: s("20px"),
          pr: s("20px"),
          //transform: focused ? "scale(1.08)" : undefined,
          boxShadow: focused
            ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.60)`
            : "none",
          borderRadius: s("24px"),
          backgroundColor: focused
            ? theme.palette.secondary.variant
            : theme.palette.primary.main,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: s("560px"),
            height: s("200px"),
            borderBottom: "1px solid",
            borderColor: focused ? "transparent" : theme.palette.white[20],
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              width: s("520px"),
              height: s("200px"),
            }}
          >
            <DGText
              tx={TI.Subheadline08tv}
              sx={{
                color: theme.palette.white[30],
                mb: s("12px"),
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 1,
              }}
            >
              {teaser.subtitle}
            </DGText>
            <DGText
              tx={TI.Subheadline02tv}
              sx={{
                color: theme.palette.white.main,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {teaser.title}
            </DGText>
            {/* mitigate the fake border added with absolute positioning above */}
            <Box sx={{ height: s("10px") }}></Box>
          </Box>
          <svg
            width="12"
            height="23"
            viewBox="0 0 12 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.0335 10.6365C11.4331 11.1303 11.4331 11.871 11.0335 12.3155L2.50903 21.7969C2.06505 22.2908 1.39907 22.2908 0.99949 21.7969C0.555508 21.3525 0.555508 20.6118 0.99949 20.1673L8.76917 11.5253L0.99949 2.83398C0.555508 2.38954 0.555508 1.6488 0.99949 1.20435C1.39907 0.710528 2.06505 0.710528 2.46463 1.20435L11.0335 10.6365Z"
              fill="white"
            />
          </svg>
        </Box>
      </Box>
    </Box>
  )
}
